import { createPinia } from "pinia";
import { createApp, watch } from "vue";
import { z } from "zod";
import App from "./App";
import { configSentry } from "./config/sentry.config";
import focus from "./directives/focus";
import environment from "./environment";
import facebookWhatsapp from "./plugins/facebook-whatsapp";
import googleAnalytics from "./plugins/google-analytics";
import { createPiniaColada } from "./plugins/pinia-colada";
import router from "./router";
import { initI18n, locale } from "./services/i18n";

import { resetStore } from "./services/pinia";
import { installUnhandledRejectionHandler } from "./utils/unhandled-rejection";
import "@/api/rest";
import "./assets/styles/index.css";

const app = createApp(App);
const pinia = createPinia();

pinia.use(resetStore);

// Suprime o ruído de `onunhandledrejection` de ApiErrors já tratados (AI-977).
// Registrado antes do Sentry para o preventDefault valer também no console.
installUnhandledRejectionHandler();

configSentry(app);

app.use(pinia);
createPiniaColada(app);

app.use(router);

app.use(facebookWhatsapp, {
  appId: environment.VITE_WHATSAPP_APP_ID,
  configId: environment.VITE_WHATSAPP_CONFIG_ID,
  version: "v22.0", // Graph API version goes here
});

if (environment.VITE_GOOGLE_ANALYTICS_ID) {
  app.use(googleAnalytics, { id: environment.VITE_GOOGLE_ANALYTICS_ID });
}

app.directive("focus", focus);

initI18n();

app.mount("#app");

// Configure Zod locale dynamically
function setZodLocale(lang: string) {
  const localeMap = lang === "pt" ? z.locales.pt() : z.locales.en();
  const requiredMessage = lang === "pt" ? "Campo obrigatório" : "Required field";

  z.config({
    ...localeMap,
    // The CSP has no 'unsafe-eval': skip zod's `new Function` probe instead of
    // raising a violation report on every first parse.
    jitless: true,
    customError: (issue) => {
      if (issue.code === "invalid_type" && issue.input === undefined) {
        return requiredMessage;
      }
    },
  });
}

setZodLocale(locale.value);
watch(locale, (newLocale) => setZodLocale(newLocale));
